/**
 * Custom Field Type Definitions
 *
 * Types for database-defined custom fields that can be attached to entities.
 * These types match the backend CustomField entity structure.
 */

// ============================================================================
// Custom Field Type Enum
// ============================================================================

/**
 * Custom field types matching backend enum
 * See: Src/DesignHub.Domain/CustomField.cs
 */
export enum CustomFieldType {
  /** Dropdown with predefined allowed values */
  AllowedValue = 0,
  /** Date picker */
  Date = 1,
  /** Text input (single or multiline) */
  Text = 2,
  /** Nested dropdown with parent-child relationships */
  NestedAllowedValue = 3,
  /** Numeric input with decimal support */
  Numeric = 4,
  /** Visual divider (not displayed as column) */
  Divider = 5,
}

// ============================================================================
// Custom Field Definition
// ============================================================================

/**
 * Metadata for a custom field definition
 * This describes the field itself, not its value on an entity
 */
export type CustomFieldDefinition = {
  /** Unique identifier for the custom field */
  customFieldId: number;
  /** Display name for the field */
  name: string;
  /** User-defined identifier (optional) */
  userDefinedId: string | null;
  /** Field type (determines rendering) */
  type: CustomFieldType;
  /** Whether the field is required */
  isMandatory: boolean;
  /** Whether suppliers can edit this field */
  isSupplierEditable: boolean;
  /** Whether the field is internal only (not visible to suppliers) */
  internalOnly: boolean;
  /** Maximum character length for text fields */
  maxChar: number | null;
  /** Whether text field should be multiline */
  isMultiLine: boolean;
  /** Number of allowed decimal places for numeric fields */
  allowedDecimals: number;
  /** Whether multiple values can be selected (for AllowedValue type) */
  isMultipleAllowedValue?: boolean;
  /** Allowed values for dropdown fields */
  allowedValues?: CustomFieldAllowedValue[];
  /** Parent custom field ID (for dependent fields) */
  parentCustomFieldId?: number | null;
  /** Parent custom field name (for display) */
  parentCustomFieldName?: string | null;
  /** True when the field is scoped to one or more brands (dl-58878). */
  hasBrandScope?: boolean;
  /** True when the field is scoped to one or more groups (dl-58878). */
  hasGroupScope?: boolean;
  /** True when the field is scoped to one or more style categories (dl-58878). */
  hasCategoryScope?: boolean;
};

/**
 * Allowed value option for dropdown custom fields
 */
export type CustomFieldAllowedValue = {
  /** Unique identifier */
  id: number;
  /** Display label */
  label: string;
  /** User-defined identifier */
  userDefinedId?: string | null;
  /** Parent ID for nested allowed values */
  parentId?: number | null;
  /** Sort order */
  sortOrder?: number;
};

// ============================================================================
// Custom Field Value
// ============================================================================

/**
 * A custom field value on an entity (e.g., Item)
 * This represents the actual value stored for a custom field
 */
export type CustomFieldValue = {
  /** Unique identifier for this field value instance */
  id: number;
  /** Reference to the custom field definition */
  customFieldId: number;
  /** Display name (from definition) */
  name: string;
  /** User-defined identifier (from definition) */
  userDefinedId: string | null;
  /** Field type (from definition) */
  type: CustomFieldType;
  /** The actual value (string representation) */
  value: string | null;
  /** Selected allowed value ID (for dropdown types) */
  itemCustomFieldAllowedValueId: number | null;
  /** Selected allowed value user-defined ID */
  customFieldAllowedValueUserDefinedID: string | null;
  /** Whether the field is required */
  isMandatory: boolean;
  /** Whether suppliers can edit this field */
  isSupplierEditable: boolean;
  /** Whether the field is internal only */
  internalOnly: boolean;
  /** Maximum character length */
  maxChar: number | null;
  /** Whether text field is multiline */
  isMultiLine: boolean;
  /** Allowed decimal places */
  allowedDecimals: number;
  /** Whether multiple values can be selected (for AllowedValue type) */
  isMultipleAllowedValue?: boolean;
  /** Allowed values for dropdown fields */
  allowedValues?: Array<{
    id: number;
    label: string;
    userDefinedId?: string | null;
    parentId?: number | null;
  }>;
  /** Parent custom field ID (for dependent fields) */
  parentCustomFieldId?: number | null;
  /** Parent custom field name (for display) */
  parentCustomFieldName?: string | null;
};

// ============================================================================
// Custom Field Entry (minimal value-only type)
// ============================================================================

/**
 * Minimal value-only representation of a custom field entry on an entity.
 * Used as the generic constraint for table column generators so that both
 * full (legacy embedded) and lean (separate meta endpoint) patterns work.
 */
export type CustomFieldEntry = {
  id: number;
  customFieldId: number;
  value: string | null;
  itemCustomFieldAllowedValueId: number | null;
  customFieldAllowedValueUserDefinedID: string | null;
  /**
   * True when this field applies to the owning row's brand/group/category scope (dl-58878).
   * Absent for entities without scope (e.g. Items); resolvers must default to true.
   */
  appliesToStyle?: boolean;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract custom field ID from a column ID
 * Handles both formats: cf_{id} and cf_{id}_{type}
 * Returns null if the column ID is not a custom field column
 */
export const parseCustomFieldColumnId = (columnId: string): number | null => {
  if (!columnId.startsWith('cf_')) return null;
  // Handle both cf_{id} and cf_{id}_{type} formats
  const parts = columnId.split('_');
  if (parts.length < 2) return null;
  const id = parseInt(parts[1], 10);
  return isNaN(id) ? null : id;
};
